import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/ConfirmDialog';
import { canWrite } from '../../utils/roles';
import { groupLogicalInventory } from '../../utils/logicalInventory';

const STATUS_LABELS = {
  draft: { label: 'Borrador', bg: 'var(--surface)', color: 'var(--text-dim)' },
  in_transit: { label: 'En tránsito', bg: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)' },
  completed: { label: 'Completado', bg: 'var(--green-dim)', color: 'var(--green)' },
};

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23' });
}

function dedupeItems(items) {
  if (!items) return [];
  const packIds = new Set(items.filter(i => i.pack_id).map(i => i.pack_id));
  return items.filter(item => {
    if (item.pack_id) return true;
    if (item.item_pack_id && packIds.has(item.item_pack_id)) return false;
    return true;
  });
}

const PackIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

function QuantityStepper({ value, max, onChange, label }) {
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const update = (nextValue) => {
    const boundedValue = Math.max(0, Math.min(max, Number.isFinite(nextValue) ? nextValue : 0));
    setDraftValue(String(boundedValue));
    onChange(boundedValue);
  };

  const commitDraft = () => {
    const parsedValue = Number.parseInt(draftValue, 10);
    update(Number.isFinite(parsedValue) ? parsedValue : 0);
  };

  return (
    <div className="location-quantity-stepper" aria-label={label}>
      <button
        type="button"
        className="location-quantity-button"
        onClick={() => update(value - 1)}
        disabled={value === 0}
        aria-label={`Quitar una unidad de ${label}`}
      >
        −
      </button>
      <div className="location-quantity-value">
        <input
          type="number"
          min="0"
          max={max}
          inputMode="numeric"
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
          aria-label={`Cantidad de ${label}`}
        />
        <span> / {max}</span>
      </div>
      <button
        type="button"
        className="location-quantity-button"
        onClick={() => update(value + 1)}
        disabled={value === max}
        aria-label={`Agregar una unidad de ${label}`}
      >
        +
      </button>
    </div>
  );
}

function LocationActionModal({ mode, location, onClose, onDone }) {
  const [locations, setLocations] = useState([]);
  const [destinationId, setDestinationId] = useState('');
  const [items, setItems] = useState([]);
  const [foundItems, setFoundItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [foundIds, setFoundIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [notes, setNotes] = useState('');
  const [mergeAll, setMergeAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const initializedReconciliationSelection = useRef(false);

  useEffect(() => {
    api.get('/locations')
      .then(({ data }) => {
        setLocations(data);
        if (mode === 'dispatch') {
          setDestinationId(String(data.find((entry) => entry.id !== location.id)?.id || ''));
        }
      })
      .catch(() => setError('No se pudieron cargar los lugares'))
      .finally(() => setLoading(false));
  }, [mode]);

  useEffect(() => {
    const isReconciliation = mode === 'reconcile';
    if (!isReconciliation && !destinationId) return;
    const timeout = setTimeout(() => {
      setLoading(true);
      const request = isReconciliation
        ? api.get(`/locations/${location.id}/reconciliation-items`, { params: { q: search } })
        : api.get(`/locations/${destinationId}/available-items`, {
          params: { source_location_id: location.id, q: search },
        });
      request
        .then(({ data }) => {
          if (isReconciliation) {
            setItems(data.registered_here || []);
            setFoundItems(data.found_elsewhere || []);
            if (!initializedReconciliationSelection.current) {
              initializedReconciliationSelection.current = true;
              setSelectedIds(new Set((data.registered_here || []).map((item) => item.id)));
            }
          } else {
            setItems(data);
            if (mergeAll) setSelectedIds(new Set(data.map((item) => item.id)));
          }
        })
        .catch((requestError) => setError(requestError.response?.data?.error || 'No se pudieron cargar los equipos'))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(timeout);
  }, [destinationId, location.id, mergeAll, mode, search]);

  useEffect(() => {
    if (mode === 'reconcile' || !search.trim()) {
      if (mode !== 'reconcile') {
        setFoundItems([]);
        setFoundIds(new Set());
      }
      return;
    }
    const timeout = setTimeout(() => {
      api.get(`/locations/${location.id}/reconciliation-items`, { params: { q: search } })
        .then(({ data }) => {
          const found = data.found_elsewhere || [];
          setFoundItems(found);
          setFoundIds((current) => new Set([...current].filter((id) => found.some((item) => item.id === id))));
        })
        .catch(() => setFoundItems([]));
    }, 180);
    return () => clearTimeout(timeout);
  }, [location.id, mode, search]);

  const setLogicalGroupQuantity = (group, setIds, quantity, resetMerge = false) => {
    if (resetMerge) setMergeAll(false);
    setIds((current) => {
      const next = new Set(current);
      const boundedQuantity = Math.max(0, Math.min(group.items.length, quantity));
      group.items.forEach((item, index) => {
        if (index < boundedQuantity) next.add(item.id);
        else next.delete(item.id);
      });
      return next;
    });
  };

  const selectedGroupQuantity = (group, ids) => group.items.reduce(
    (total, item) => total + (ids.has(item.id) ? 1 : 0),
    0,
  );

  const selectAllForMerge = () => {
    setMergeAll(true);
    setSelectedIds(new Set(items.map((item) => item.id)));
    setFoundIds(new Set());
  };

  const handleConfirm = async () => {
    if (!selectedIds.size && !foundIds.size) {
      setError('Confirmá al menos un equipo');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const endpoint = mode === 'reconcile'
        ? `/locations/${location.id}/reconcile`
        : mergeAll
          ? `/locations/${location.id}/merge`
          : foundIds.size
            ? `/locations/${location.id}/move-found`
          : `/locations/${destinationId}/move`;
      const body = mode === 'reconcile'
        ? { confirmed_item_ids: [...selectedIds], found_item_ids: [...foundIds], notes }
        : mergeAll
          ? { target_id: Number(destinationId) }
          : foundIds.size
            ? { to_location_id: Number(destinationId), item_ids: [...selectedIds], found_item_ids: [...foundIds], notes }
          : { from_location_id: Number(location.id), item_ids: [...selectedIds], notes };
      if (mergeAll) await api.patch(endpoint, body);
      else await api.post(endpoint, body);
      onDone();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo registrar el movimiento');
      setSaving(false);
    }
  };

  const isReconciliation = mode === 'reconcile';
  const sourceOptions = locations.filter((entry) => entry.id !== location.id);
  const groupedItems = groupLogicalInventory(items);
  const groupedFoundItems = groupLogicalInventory(foundItems);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680, width: 'min(680px, calc(100vw - 24px))' }} onClick={(event) => event.stopPropagation()}>
        <div className="modal-title">
          {isReconciliation ? `Conciliar stock en ${location.name}` : `Trasladar desde ${location.name}`}
        </div>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '4px 0 16px' }}>
          {isReconciliation
            ? 'La conciliación parte del stock registrado en este lugar y lo marca completo. Desmarcá solo lo que no esté físicamente aquí. Si encontrás un equipo extra, buscálo y sumalo como corrección.'
            : 'Seleccioná el stock que sale de este lugar. Si encontrás un equipo que el sistema atribuye a otro lugar, buscálo: se concilia aquí y se traslada a destino en la misma acción.'}
        </p>
        {error && <div className="inv-error" style={{ marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: isReconciliation ? '1fr' : 'minmax(0, 1fr) auto', gap: 8, marginBottom: 10 }}>
          {!isReconciliation && (
            <select className="input" value={destinationId} onChange={(event) => { setDestinationId(event.target.value); setSelectedIds(new Set()); setMergeAll(false); }}>
              <option value="">Seleccionar destino</option>
              {sourceOptions.map((entry) => <option value={entry.id} key={entry.id}>{entry.name}</option>)}
            </select>
          )}
          {!isReconciliation && <button type="button" className={`btn ${mergeAll ? 'btn-primary' : 'btn-ghost'}`} onClick={selectAllForMerge}>Trasladar todos los equipos</button>}
        </div>
        {mergeAll && (
          <div style={{ marginBottom: 10, padding: '10px 12px', border: '1px solid var(--yellow, #b7791f)', borderRadius: 6, background: 'var(--yellow-dim, #fff8e1)', color: 'var(--text)' }}>
            Se trasladará <strong>TODO</strong> el stock a destino. Al confirmarlo, <strong>{location.name}</strong> dejará de existir como locación independiente.
          </div>
        )}
        <input className="input" placeholder={isReconciliation ? 'Buscar equipo encontrado aquí...' : 'Buscar equipo o categoría...'} value={search} onChange={(event) => setSearch(event.target.value)} />
        <div style={{ border: '1px solid var(--border)', marginTop: 8, maxHeight: 300, overflowY: 'auto', borderRadius: 6 }}>
          {loading ? <div style={{ padding: 16, color: 'var(--text-dim)' }}>Cargando equipos...</div> : items.length === 0 && !isReconciliation ? (
            <div style={{ padding: 16, color: 'var(--text-dim)' }}>
              No hay unidades disponibles en este lugar.
            </div>
          ) : <>
          {isReconciliation && <div style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', background: 'var(--surface2)' }}>STOCK REGISTRADO AQUÍ</div>}
          {isReconciliation && items.length === 0 ? <div style={{ padding: 12, color: 'var(--text-dim)', fontSize: 13 }}>No hay equipos registrados en este lugar.</div> : groupedItems.map((group) => {
            const quantity = selectedGroupQuantity(group, selectedIds);
            return (
            <div key={group.key} className="location-selection-row">
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{group.name}</strong>
                <span className="location-selection-available">{group.items.length} disponible{group.items.length !== 1 ? 's' : ''}</span>
              </span>
              <QuantityStepper
                label={group.name}
                value={quantity}
                max={group.items.length}
                onChange={(nextQuantity) => setLogicalGroupQuantity(group, setSelectedIds, nextQuantity, true)}
              />
            </div>
          )})}
          {search && <>
            <div style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', background: 'var(--surface2)', borderTop: '1px solid var(--border)' }}>
              {isReconciliation ? 'EQUIPOS ENCONTRADOS AQUÍ' : 'ENCONTRADOS AQUÍ, REGISTRADOS EN OTRO LUGAR'}
            </div>
            {foundItems.length === 0 ? <div style={{ padding: 12, color: 'var(--text-dim)', fontSize: 13 }}>No se encontraron equipos registrados en otros lugares.</div> : groupedFoundItems.map((group) => {
              const quantity = selectedGroupQuantity(group, foundIds);
              return (
              <div key={`found-${group.key}`} className="location-selection-row">
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong>{group.name}</strong>
                  <span style={{ display: 'block', color: 'var(--yellow, #b7791f)', fontSize: 11, marginTop: 2 }}>
                    Registrado en {group.recorded_location_name}{isReconciliation ? '' : `. Se conciliará en ${location.name} antes del traslado.`}
                  </span>
                </span>
                <QuantityStepper
                  label={group.name}
                  value={quantity}
                  max={group.items.length}
                  onChange={(nextQuantity) => setLogicalGroupQuantity(group, setFoundIds, nextQuantity)}
                />
              </div>
            )})}
          </>}
          </>}
        </div>
        <textarea className="input" rows="2" style={{ resize: 'vertical', marginTop: 10 }} placeholder="Comentario opcional" value={notes} onChange={(event) => setNotes(event.target.value)} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <span style={{ color: 'var(--text-dim)', fontSize: 13 }}>
            {mergeAll
              ? 'TODO el stock seleccionado'
              : `${selectedIds.size + foundIds.size} unidad${selectedIds.size + foundIds.size !== 1 ? 'es' : ''} ${isReconciliation ? 'confirmada' : 'seleccionada'}${selectedIds.size + foundIds.size !== 1 ? 's' : ''}`}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleConfirm} disabled={saving || (!selectedIds.size && !foundIds.size) || (!isReconciliation && !destinationId)}>
              {saving ? (mergeAll ? 'Trasladando todos...' : 'Guardando...') : isReconciliation ? 'Confirmar conciliación' : mergeAll ? 'Trasladar todos los equipos' : 'Trasladar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── TransferRow (accordion) ───────────────────────────────────────────────────

function TransferRow({ t }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const s = STATUS_LABELS[t.status] || {};

  const itemNodes = items === null ? null : groupLogicalInventory(dedupeItems(items)).map((group) => (
    <div key={group.key} style={{ fontSize: 12, color: 'var(--text-dim)', padding: '1px 0', display: 'flex', alignItems: 'center', gap: 5 }}>
      {group.pack_id && <PackIcon />}
      <strong>{group.items.length} ×</strong>
      {group.name}
      {group.items.some((item) => !item.inventory_item_id && !item.pack_id) && (
        <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)', fontWeight: 600 }}>Sin consolidar</span>
      )}
    </div>
  ));

  const handleToggle = async () => {
    if (!expanded && items === null) {
      setLoadingItems(true);
      try {
        const { data } = await api.get(`/transfers/${t.id}`);
        setItems(data.items || []);
      } catch (e) {
        console.error(e);
        setItems([]);
      } finally {
        setLoadingItems(false);
      }
    }
    setExpanded(prev => !prev);
  };

  return (
    <div style={{
      borderRadius: 6, border: '1px solid var(--border)',
      background: 'var(--surface2)', overflow: 'hidden',
    }}>
      <div
        onClick={handleToggle}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500 }}>{t.vehicle || '—'}</span>
            <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>{t.from_location_name} → {t.to_location_name}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span>{formatDate(t.created_at)}</span>
            <span>· {t.item_count} ítem{t.item_count !== 1 ? 's' : ''}</span>
            {t.kind && t.kind !== 'transfer' && (
              <span>· {{
                initial_reconciliation: 'Conciliación inicial',
                location_correction: 'Corrección de ubicación',
                direct_move: 'Movimiento directo',
              }[t.kind] || 'Movimiento'}</span>
            )}
            {t.event_name && <span>· {t.event_name}</span>}
          </div>
        </div>
        <span style={{
          fontSize: 11, padding: '2px 7px', borderRadius: 4, flexShrink: 0,
          background: s.bg, color: s.color, fontWeight: 600,
        }}>
          {s.label}
        </span>
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-dim)" strokeWidth="2"
          style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {loadingItems ? (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '4px 0' }}>Cargando ítems...</div>
          ) : (items || []).length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '4px 0' }}>Sin ítems registrados.</div>
          ) : itemNodes}
        </div>
      )}
    </div>
  );
}

// ── LocationDetail ────────────────────────────────────────────────────────────

function LocationDetail({ locationId, onBack, onMutated, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [actionMode, setActionMode] = useState(null);
  const [stockTab, setStockTab] = useState('stock');
  const [stockSearch, setStockSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    setData(null);
    api.get(`/locations/${locationId}`)
      .then(({ data }) => setData(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [locationId]);

  const handleDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await api.delete(`/locations/${locationId}`);
      onMutated();
    } catch (e) {
      const count = e.response?.data?.item_count;
      setDeleteError(count
        ? `Hay ${count} equipo${count !== 1 ? 's' : ''} en esta locación. Movelos antes de eliminar.`
        : (e.response?.data?.error || 'Error al eliminar'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-dim)' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }} />
        Cargando...
      </div>
    );
  }
  if (!data) return null;

  const allItems = data.items || [];
  const normalizedSearch = stockSearch.trim().toLocaleLowerCase('es');
  const visibleItems = allItems.filter((item) => {
    const searchable = [item.name, item.category, item.subcategory, item.pack_name]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('es');
    if (normalizedSearch && !searchable.includes(normalizedSearch)) return false;
    if (stockFilter === 'operational') return !item.unconsolidated && item.is_operational;
    if (stockFilter === 'unconsolidated') return item.unconsolidated;
    return true;
  });

  // Group by category -> subcategory -> { packs: { packId: { name, items[] } }, items: [] }.
  // This mirrors Inventory's data language while keeping the location as the source of truth.
  const grouped = {};
  for (const item of visibleItems) {
    const cat = item.category || 'Sin categoría';
    const sub = item.subcategory || 'General';
    if (!grouped[cat]) grouped[cat] = {};
    if (!grouped[cat][sub]) grouped[cat][sub] = { packs: {}, items: [] };
    const bucket = grouped[cat][sub];
    if (item.pack_id) {
      if (!bucket.packs[item.pack_id]) {
        bucket.packs[item.pack_id] = { name: item.pack_name || `Pack ${item.pack_id}`, items: [] };
      }
      bucket.packs[item.pack_id].items.push(item);
    } else {
      bucket.items.push(item);
    }
  }

  const consolidatedItemCount = allItems.filter(i => !i.unconsolidated).length;
  const unconsolidatedItemCount = allItems.filter(i => i.unconsolidated).length;
  const itemCount = allItems.length;

  const groupStockModels = (items) => {
    const models = new Map();
    for (const item of items) {
      // Legacy checklist rows do not represent an identified physical unit, so
      // they must remain separate until a reconciliation turns them into stock.
      const key = item.unconsolidated
        ? `unconsolidated:${item.id}`
        : [item.name, item.category, item.subcategory].join('::');
      if (!models.has(key)) models.set(key, { ...item, units: [] });
      models.get(key).units.push(item);
    }
    return Array.from(models.values());
  };

  return (
    <div>
      <div className="location-breadcrumb">Lugares activos <span>/</span> {data.name}</div>
      {/* Back + header */}
      <div className="location-detail-header">
        <button className="btn btn-ghost btn-icon" onClick={onBack} title="Volver" style={{ flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <h2 className="location-detail-title">
            {data.name}
            {data.is_home && (
              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: 'var(--accent-dim, #312e81)', color: 'var(--accent, #6366f1)', fontWeight: 600 }}>
                Depósito
              </span>
            )}
          </h2>
          <p className="location-detail-subtitle">
            {itemCount} equipo{itemCount !== 1 ? 's' : ''}
            {unconsolidatedItemCount > 0 && (
              <span style={{ color: 'var(--yellow, #f59e0b)', marginLeft: 6 }}>
                ({unconsolidatedItemCount} sin consolidar)
              </span>
            )}
          </p>
        </div>
        {isAdmin && (
          <div className="location-detail-actions">
            <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => setActionMode('dispatch')}>
              Trasladar desde aquí
            </button>
            <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={() => setActionMode('reconcile')}>Conciliar stock</button>
            {!data.is_home && consolidatedItemCount === 0 && (
              <button
                className="btn btn-ghost"
                style={{ fontSize: 13, color: 'var(--red, #ef4444)' }}
                onClick={() => setConfirmDelete(true)}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="location-detail-tabs" role="tablist" aria-label="Vista de la ubicación">
        <button type="button" className={stockTab === 'stock' ? 'is-active' : ''} onClick={() => setStockTab('stock')}>Stock actual</button>
        <button type="button" className={stockTab === 'history' ? 'is-active' : ''} onClick={() => setStockTab('history')}>Historial</button>
      </div>

      {stockTab === 'stock' ? (
        <div className="location-stock-section">
          <div className="location-stock-toolbar">
            <input
              className="input"
              value={stockSearch}
              onChange={(event) => setStockSearch(event.target.value)}
              placeholder="Buscar equipo o categoría..."
              aria-label="Buscar stock en la ubicación"
            />
            <div className="location-stock-filters" role="group" aria-label="Filtrar stock">
              <button type="button" className={stockFilter === 'all' ? 'is-active' : ''} onClick={() => setStockFilter('all')}>Todos</button>
              <button type="button" className={stockFilter === 'operational' ? 'is-active' : ''} onClick={() => setStockFilter('operational')}>Operativos</button>
              {!data.is_home && <button type="button" className={stockFilter === 'unconsolidated' ? 'is-active' : ''} onClick={() => setStockFilter('unconsolidated')}>Sin conciliar</button>}
            </div>
            <span className="location-stock-count">{visibleItems.length} de {itemCount} equipos</span>
          </div>
          {itemCount === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>No hay equipos en esta locación.</p>
        ) : visibleItems.length === 0 ? (
          <p className="location-empty-result">No hay equipos que coincidan con estos filtros.</p>
        ) : (
          <div className="location-stock-groups">
            {Object.entries(grouped).map(([cat, subs]) => (
              <div key={cat} className="location-stock-group">
                <div className="location-stock-group-title">
                  <span>{cat}</span>
                  <span>{Object.values(subs).reduce((total, bucket) => total + bucket.items.length + Object.values(bucket.packs).reduce((packTotal, pack) => packTotal + pack.items.length, 0), 0)} u.</span>
                </div>
                {Object.entries(subs).map(([sub, { packs, items: soloItems }], subIdx) => (
                  <div key={sub} className={`location-stock-subgroup${subIdx > 0 ? ' is-separated' : ''}`}>
                    <div className="location-stock-subtitle"><span>{sub}</span></div>
                    <div className="location-stock-rows">
                      {Object.entries(packs).map(([packId, pack]) => {
                    const hasIssue = pack.items.some(i => !i.is_operational);
                    const hasIncident = !hasIssue && pack.items.some(i => i.has_open_incident);
                    return (
                      <div key={packId} className="location-stock-row">
                        <div className="location-stock-item-name">
                          <PackIcon />
                          <span className="location-stock-unit">{pack.items.length}</span>
                          <span className="location-stock-name">{pack.name}</span>
                        </div>
                        <div className="location-stock-row-meta">
                          <span>{pack.items.length} ítems</span>
                          {hasIssue && (
                            <span className="location-status is-danger">
                              No operativo
                            </span>
                          )}
                          {hasIncident && (
                            <span className="location-status is-warning">
                              Incidencia
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                      {groupStockModels(soloItems).map(model => {
                        const units = model.units;
                        const total = units.length;
                        // Los ítems de texto van sin multiplicador: son líneas
                        // del pedido, no unidades contadas. La cantidad ya viene
                        // escrita adentro ("2 FBT Blancos"), así que un número
                        // adelante se contradice con el propio texto.
                        //
                        // Tampoco serviría poner el neto: cuenta renglones de
                        // traslado, no equipos, y llega a 2 tanto si el checklist
                        // tenía dos líneas iguales como si mandaste la misma dos
                        // veces —que son cosas distintas y el sistema no las
                        // distingue—. Si hace falta saber cuántos envíos hubo,
                        // eso se ve en el historial de traslados, con su fecha.
                        const muestraCantidad = !model.unconsolidated;
                        const hasIssue = units.some(item => !item.unconsolidated && !item.is_operational);
                        const hasIncident = !hasIssue && units.some(item => !item.unconsolidated && item.has_open_incident);
                        return (
                          <div key={model.unconsolidated ? model.id : `${model.name}:${model.category}:${model.subcategory}`} className="location-stock-row">
                            <div className="location-stock-item-name">
                              {muestraCantidad && <span className="location-stock-quantity">{total} ×</span>}
                              <div className="location-stock-model">
                                <span className="location-stock-name">{model.name}</span>
                              </div>
                            </div>
                            <div className="location-stock-row-meta">
                              {model.unconsolidated && <span className="location-status is-warning">Sin consolidar</span>}
                              {hasIssue && <span className="location-status is-danger">No operativo</span>}
                              {hasIncident && <span className="location-status is-warning">Incidencia</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        </div>
      ) : (
      <div className="location-history-section">
        {(data.transfers || []).length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>Sin movimientos registrados.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {data.transfers.map(t => <TransferRow key={t.id} t={t} />)}
          </div>
        )}
      </div>
      )}

      {actionMode && (
        <LocationActionModal
          mode={actionMode}
          location={data}
          onClose={() => setActionMode(null)}
          onDone={() => { setActionMode(null); onMutated(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Eliminar locación"
          message={`¿Eliminar la locación "${data.name}"? Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {deleteError && (
        <div
          className="modal-overlay"
          onClick={() => setDeleteError('')}
        >
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">No se puede eliminar</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 20 }}>{deleteError}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setDeleteError('')}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── LocationsPage ─────────────────────────────────────────────────────────────

function ActivateMasterLocationModal({ onClose, onSelected }) {
  const [lugares, setLugares] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/locations/master-lugares')
      .then(({ data }) => setLugares(data))
      .catch((requestError) => setError(requestError.response?.data?.error || 'No se pudieron cargar los lugares'))
      .finally(() => setLoading(false));
  }, []);

  const normalizedSearch = search.trim().toLocaleLowerCase('es');
  const visibleLugares = lugares.filter((lugar) => (
    !normalizedSearch || [lugar.name, lugar.address]
      .filter(Boolean)
      .some((value) => value.toLocaleLowerCase('es').includes(normalizedSearch))
  ));

  const selectLugar = async (lugar) => {
    setSelectingId(lugar.lugar_id);
    setError('');
    try {
      await onSelected(lugar);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo habilitar el lugar en Logística');
      setSelectingId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>Agregar lugar desde maestro</h3>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-dim)' }}>
            Seleccioná un lugar ya definido en la configuración global. Se habilitará en Logística con stock vacío para conciliarlo o registrar traslados.
          </p>
          {error && <div className="alert alert-error">{error}</div>}
          <input
            className="form-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar lugar..."
            autoFocus
          />
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 7 }}>
            {loading ? (
              <div style={{ padding: 20, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando lugares...</div>
            ) : visibleLugares.length === 0 ? (
              <div style={{ padding: 20, color: 'var(--text-dim)', textAlign: 'center' }}>No hay lugares para mostrar.</div>
            ) : visibleLugares.map((lugar) => (
              <button
                key={lugar.lugar_id}
                type="button"
                className="locations-master-option"
                onClick={() => selectLugar(lugar)}
                disabled={selectingId !== null}
              >
                <span>
                  <strong>{lugar.name}</strong>
                  {lugar.address && <small>{lugar.address}</small>}
                </span>
                <small>{selectingId === lugar.lugar_id ? 'Habilitando...' : (lugar.location_id ? `${lugar.item_count} equipo${Number(lugar.item_count) === 1 ? '' : 's'}` : 'Sin stock')}</small>
              </button>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

export default function LocationsPage() {
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'lugares-activos');
  const canActivateLocation = canWrite(user, 'lugares-activos');

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [scope, setScope] = useState('all');
  const [creatingLocation, setCreatingLocation] = useState(false);

  // La búsqueda se resuelve en el backend (por nombre de lugar o de equipo),
  // con debounce para no pegarle en cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Normalmente priorizamos los lugares operativos. Para conciliar un
      // lugar vacío necesitamos traer también el maestro completo.
      if (scope !== 'empty') params.set('with_stock', 'true');
      if (debouncedSearch) params.set('search', debouncedSearch);
      const { data } = await api.get(`/locations?${params.toString()}`);
      setLocations(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, scope]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selectedId && locations.length > 0 && !locations.find(l => l.id === selectedId)) {
      setSelectedId(null);
    }
  }, [locations, selectedId]);

  const handleMutated = () => {
    setSelectedId(null);
    load();
  };

  const handleMasterLocationSelected = async (lugar) => {
    const location = lugar.location_id
      ? { id: lugar.location_id }
      : (await api.post(`/locations/from-lugar/${lugar.lugar_id}`)).data;
    setCreatingLocation(false);
    setScope('empty');
    setSearch('');
    setDebouncedSearch('');
    setSelectedId(Number(location.id));
    await load();
  };

  const visibleLocations = locations.filter((location) => {
    // Un lugar que recibió una lista de texto no está vacío, aunque no tenga
    // ninguna unidad de inventario: tiene equipo, sólo que sin consolidar.
    if (scope === 'empty') {
      return Number(location.item_count) === 0 && Number(location.unconsolidated_count || 0) === 0;
    }
    if (scope === 'home') return location.is_home;
    if (scope === 'other') return !location.is_home;
    return true;
  });

  return (
    <div className="locations-page">
      {selectedId ? (
        <LocationDetail
          locationId={selectedId}
          onBack={() => setSelectedId(null)}
          onMutated={handleMutated}
          isAdmin={isAdmin}
        />
      ) : (
        <>
          <div className="page-header">
            <div>
              <h1 className="page-title">Lugares activos</h1>
              <p className="page-subtitle">Stock físico y movimientos por ubicación</p>
            </div>
            {canActivateLocation && (
              <button className="btn btn-primary" onClick={() => setCreatingLocation(true)}>+ Agregar lugar</button>
            )}
          </div>

          <div className="locations-toolbar">
            <input
              className="input"
              style={{ flex: 1, minWidth: 200 }}
              placeholder="Buscar por lugar o equipo..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div className="locations-scope" role="group" aria-label="Filtrar lugares">
              <button className={`btn ${scope === 'all' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScope('all')}>Todos</button>
              <button className={`btn ${scope === 'home' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScope('home')}>Depósito</button>
              <button className={`btn ${scope === 'other' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScope('other')}>En lugares</button>
              <button className={`btn ${scope === 'empty' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setScope('empty')}>Sin equipos</button>
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              Cargando...
            </div>
          ) : visibleLocations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dim)', fontSize: 14 }}>
              {debouncedSearch ? 'Sin resultados.' : scope === 'empty' ? 'No hay lugares sin equipos.' : 'No hay lugares con equipos en este momento.'}
            </div>
          ) : (
            <div className="locations-table">
              <div className="locations-table-head">
                <span>Lugar</span>
                <span>Tipo</span>
                <span>Equipos</span>
                <span>Estado</span>
                <span aria-label="Abrir" />
              </div>
              {visibleLocations.map(loc => (
                <div
                  key={loc.id}
                  onClick={() => setSelectedId(loc.id)}
                  className="locations-table-row"
                >
                  <div className="locations-place-name">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span>{loc.name}</span>
                  </div>
                  <div className="locations-type">
                      {loc.is_home && (
                        <span className="location-type-badge">
                          Depósito
                        </span>
                      )}
                  </div>
                  <div className="locations-quantity">
                    {loc.item_count} equipo{loc.item_count !== 1 ? 's' : ''}
                  </div>
                  <div className="locations-status">
                    {loc.unconsolidated_count > 0 ? <span className="location-status is-warning">{loc.unconsolidated_count} a conciliar</span> : <span className="location-status is-ok">Conciliado</span>}
                  </div>
                  <svg className="locations-open-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              ))}
            </div>
          )}
          {creatingLocation && <ActivateMasterLocationModal onClose={() => setCreatingLocation(false)} onSelected={handleMasterLocationSelected} />}
        </>
      )}

    </div>
  );
}
